import { useCallback, useRef, useState } from 'react';
import type { ScheduleDay } from '../schedule';
import {
  createScenarioEntry,
  DEFAULT_SCENARIO_VALUES,
  getDirtyScenario,
  normalizeCallsDisable,
  SCENARIO_TYPES,
} from './scenarioSettingsState';
import type {
  CacheHit,
  DirtyScenario,
  ScenarioCacheEntry,
  ScenarioCacheValues,
  ScenarioType,
  ServerScenarioValues,
} from './scenarioSettingsState';

export { normalizeCallsDisable } from './scenarioSettingsState';
export type {
  CacheHit,
  DirtyScenario,
  ScenarioType,
  ScenarioValues,
} from './scenarioSettingsState';

export const useScenarioSettings = () => {
  const [scenarioType, setScenarioTypeState] = useState<ScenarioType>('slot');
  const activeScenarioRef = useRef<ScenarioType>('slot');
  const [callsMaxTries, setCallsMaxTries] = useState(3);
  const [callsIntervalMinutes, setCallsIntervalMinutes] = useState(60);
  const [callsDisable, setCallsDisableState] = useState(false);
  const [serverRevision, setServerRevision] = useState(0);
  const cacheRef = useRef<Partial<Record<ScenarioType, ScenarioCacheEntry>>>(
    {},
  );
  const serverRef = useRef<Partial<Record<ScenarioType, ScenarioCacheEntry>>>(
    {},
  );

  const pendingActivityEditsRef = useRef<
    Partial<Record<ScenarioType, boolean>>
  >({});

  const setCallsDisable = useCallback((value: boolean | null | undefined) => {
    const normalized = normalizeCallsDisable(value);
    const type = activeScenarioRef.current;
    if (!serverRef.current[type]) {
      pendingActivityEditsRef.current[type] = normalized;
    }
    setCallsDisableState(normalized);
  }, []);

  const setScenarioType = useCallback((type: ScenarioType) => {
    activeScenarioRef.current = type;
    setScenarioTypeState(type);
  }, []);

  const setServerSnapshot = useCallback(
    (type: ScenarioType, values: ServerScenarioValues) => {
      serverRef.current[type] = createScenarioEntry(values);
      setServerRevision((revision) => revision + 1);
    },
    [],
  );

  const updateServerScheduleSnapshot = useCallback(
    (
      type: ScenarioType,
      scheduleId: string | null,
      schedule: ScheduleDay[],
      holidays: string[],
    ) => {
      serverRef.current[type] = createScenarioEntry({
        ...(serverRef.current[type] ?? DEFAULT_SCENARIO_VALUES),
        callsScheduleId: scheduleId,
        callSchedule: schedule,
        callHolidays: holidays,
      });
      setServerRevision((revision) => revision + 1);
    },
    [],
  );

  const cacheValues = useCallback(
    (type: ScenarioType, values: ScenarioCacheValues) => {
      cacheRef.current[type] = createScenarioEntry(
        values,
        cacheRef.current[type],
      );
    },
    [],
  );

  const cacheCurrentValues = useCallback(() => {
    cacheValues(scenarioType, {
      callsMaxTries,
      callsIntervalMinutes,
      callsDisable,
    });
  }, [
    cacheValues,
    scenarioType,
    callsMaxTries,
    callsIntervalMinutes,
    callsDisable,
  ]);

  const cacheCurrentValuesWithSchedule = useCallback(
    (
      scheduleId: string | null,
      schedule: ScheduleDay[],
      holidays: string[],
    ) => {
      const server = serverRef.current[scenarioType];
      const scheduleLoaded =
        server !== undefined &&
        server.callSchedule !== null &&
        server.callHolidays !== null;
      cacheValues(scenarioType, {
        callsMaxTries,
        callsIntervalMinutes,
        callsDisable,
        callsScheduleId: scheduleLoaded
          ? scheduleId
          : (server?.callsScheduleId ?? null),
        callSchedule: scheduleLoaded ? schedule : null,
        callHolidays: scheduleLoaded ? holidays : null,
      });
    },
    [
      cacheValues,
      scenarioType,
      callsMaxTries,
      callsIntervalMinutes,
      callsDisable,
    ],
  );

  const applyFromCache = useCallback((type: ScenarioType): CacheHit | null => {
    const cached = cacheRef.current[type];
    if (!cached) return null;
    setCallsMaxTries(cached.callsMaxTries);
    setCallsIntervalMinutes(cached.callsIntervalMinutes);
    setCallsDisableState(cached.callsDisable);
    return {
      settingsLoaded: serverRef.current[type] !== undefined,
      callsScheduleId: cached.callsScheduleId,
      callSchedule: cached.callSchedule,
      callHolidays: cached.callHolidays,
    };
  }, []);

  const applyLoadedValues = useCallback(
    (type: ScenarioType, values: ScenarioCacheValues) => {
      const callsDisable =
        pendingActivityEditsRef.current[type] ?? values.callsDisable;
      delete pendingActivityEditsRef.current[type];
      cacheValues(type, { ...values, callsDisable });
      if (type !== activeScenarioRef.current) return;
      setCallsMaxTries(values.callsMaxTries);
      setCallsIntervalMinutes(values.callsIntervalMinutes);
      setCallsDisableState(callsDisable);
    },
    [cacheValues],
  );

  const getCached = useCallback(
    (type: ScenarioType): ScenarioCacheEntry | null =>
      cacheRef.current[type] ?? null,
    [],
  );

  const getCachedSlot = useCallback(
    (): ScenarioCacheEntry =>
      cacheRef.current.slot ?? createScenarioEntry(DEFAULT_SCENARIO_VALUES),
    [],
  );

  const getDirtyScenarios = useCallback(
    (
      activeScheduleId: string | null,
      activeSchedule: ScheduleDay[],
      activeHolidays: string[],
    ): DirtyScenario[] => {
      const result: DirtyScenario[] = [];
      for (const type of SCENARIO_TYPES) {
        const server = serverRef.current[type];
        if (!server) continue;
        const current =
          type === scenarioType
            ? createScenarioEntry({
                callsMaxTries,
                callsIntervalMinutes,
                callsDisable,
                callsScheduleId: activeScheduleId,
                callSchedule: activeSchedule,
                callHolidays: activeHolidays,
              })
            : cacheRef.current[type];
        if (!current) continue;
        const dirty = getDirtyScenario(type, current, server);
        if (dirty) result.push(dirty);
      }
      return result;
    },
    [
      scenarioType,
      callsMaxTries,
      callsIntervalMinutes,
      callsDisable,
      serverRevision,
    ],
  );

  const commitDirtyToServer = useCallback(
    (dirtyScenarios: DirtyScenario[]) => {
      for (const dirty of dirtyScenarios) {
        const entry = createScenarioEntry(dirty);
        serverRef.current[dirty.type] = entry;
        if (dirty.type !== scenarioType) cacheRef.current[dirty.type] = entry;
      }
    },
    [scenarioType],
  );

  return {
    scenarioType,
    setScenarioType,
    callsMaxTries,
    setCallsMaxTries,
    callsIntervalMinutes,
    setCallsIntervalMinutes,
    callsDisable,
    setCallsDisable,
    cacheCurrentValues,
    cacheCurrentValuesWithSchedule,
    cacheValues,
    applyFromCache,
    applyLoadedValues,
    getCached,
    getCachedSlot,
    setServerSnapshot,
    updateServerScheduleSnapshot,
    getDirtyScenarios,
    commitDirtyToServer,
  };
};
